/**
 * useMicCheck — reusable 3-second non-destructive microphone level test.
 *
 * Exposes `{ checking, peak, durationMs, start, stop }` so any surface in
 * the app can drop in a mic-check UI without re-implementing the
 * MediaStream → AnalyserNode boilerplate.
 *
 * Behaviour mirrors the inline check in RecordReferenceButton.jsx
 * (iter-161) — same constraint fallback, same dBFS verdict thresholds,
 * same 3-second window. The toast verdicts are emitted by the hook so
 * call-sites get consistent UX for free.
 *
 * iter-162 — extracted from RecordReferenceButton.jsx so the DAW
 * transport bar can expose a global "Mic Check" pill that works
 * regardless of which channel is selected.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const CHECK_WINDOW_MS = 3000;
const SILENT_THRESHOLD_LINEAR = 1e-4; // -80 dBFS
const QUIET_THRESHOLD_DB = -40;
const CLIP_THRESHOLD_DB = -3;

export default function useMicCheck({ onVerdict, suppressToasts = false } = {}) {
  const [checking, setChecking] = useState(false);
  const [peak, setPeak] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const timerRef = useRef(null);
  const peakRef = useRef(0);

  const stop = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { void e; }
    try { ctxRef.current?.close(); } catch (e) { void e; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    streamRef.current = null;
    ctxRef.current = null;
    analyserRef.current = null;
  };

  const start = async () => {
    if (checking) return;
    setChecking(true);
    setPeak(0);
    setDurationMs(0);
    peakRef.current = 0;
    let stream;
    try {
      // Same defensive constraint stack as iter-160 / iter-161 — strict
      // pin removed because some Chrome/Linux/USB combos deliver a
      // zero-sample stream when given strict rate/size/channelCount.
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            googEchoCancellation: false,
            googAutoGainControl: false,
            googNoiseSuppression: false,
          },
          video: false,
        });
      } catch (gumErr) {
        console.warn("[mic-check] strict getUserMedia failed, retrying minimal:", gumErr);
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const startAt = Date.now();

      const sample = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let localPeak = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = Math.abs((buf[i] - 128) / 128);
          if (v > localPeak) localPeak = v;
        }
        if (localPeak > peakRef.current) peakRef.current = localPeak;
        setPeak(peakRef.current);
        setDurationMs(Date.now() - startAt);
        rafRef.current = requestAnimationFrame(sample);
      };
      sample();

      timerRef.current = setTimeout(() => {
        const finalPeak = peakRef.current;
        const peakDb = finalPeak > 0 ? 20 * Math.log10(finalPeak) : -Infinity;
        const peakLabel = finalPeak > 0 ? `${peakDb.toFixed(1)} dBFS` : "−∞ dBFS";
        stop();
        setChecking(false);
        let verdict;
        if (finalPeak < SILENT_THRESHOLD_LINEAR) {
          verdict = "silent";
          if (!suppressToasts) toast.error("Mic is silent", {
            description: `Peak ${peakLabel} — no audio from your microphone. Check OS input level, that the right device is selected, and that the mic isn't muted at the hardware level.`,
            duration: 12000,
          });
        } else if (peakDb < QUIET_THRESHOLD_DB) {
          verdict = "quiet";
          // iter-163 — softer messaging. -42 dBFS IS recordable, it'll
          // just be quiet. The existing Auto-level button on the preview
          // controls boosts a quiet take up to -3 dBFS in one click, so
          // this is a heads-up not a failure. Pre-iter-163 wording read
          // "Mic is too quiet" which alarmed users into thinking the mic
          // didn't work at all — even though they were getting signal.
          if (!suppressToasts) toast.info("Mic is recording — quietly", {
            description: `Peak ${peakLabel}. Your mic IS working — the signal is just low. You can still record now and tap "Auto-level" after the take to boost it to -3 dBFS automatically. To get a louder signal: raise your OS mic input gain (System Preferences → Sound → Input on Mac · Settings → Sound → Input on Windows) or move closer to the mic.`,
            duration: 14000,
          });
        } else if (peakDb > CLIP_THRESHOLD_DB) {
          verdict = "clipping";
          if (!suppressToasts) toast.warning("Mic is clipping", {
            description: `Peak ${peakLabel} — back off the input gain or step away from the mic before recording.`,
            duration: 10000,
          });
        } else {
          verdict = "good";
          if (!suppressToasts) toast.success("Mic sounds good", {
            description: `Peak ${peakLabel} — you're in the safe range. Ready to record.`,
            duration: 6000,
          });
        }
        if (typeof onVerdict === "function") {
          try { onVerdict({ verdict, peak: finalPeak, peakDb }); } catch (e) { void e; }
        }
      }, CHECK_WINDOW_MS);
    } catch (e) {
      console.error("[mic-check] failed", e);
      stop();
      setChecking(false);
      if (!suppressToasts) toast.error("Microphone unavailable", {
        description: e?.name === "NotAllowedError"
          ? "Allow mic permission in the browser bar and try again."
          : (e?.message || "No microphone detected."),
      });
    }
  };

  useEffect(() => () => stop(), []);

  return { checking, peak, durationMs, start, stop };
}
